import React from 'react'
import {
    Box,
    TextField,
    Dialog,
    Typography,
    Grid,
    Button,
} from '@mui/material'
import { editPlanFields } from 'config'
import Divider from '@mui/material/Divider'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useAddPlanMutation, useUpdatePlanMutation } from 'services/userPlan-service';
import toast from 'react-hot-toast';

export const Editplan = ({ open, onClose, mode, data, refetch }) => {

    const initialValues = {}
    const [addPlan] = useAddPlanMutation();
    const [updatePlan] = useUpdatePlanMutation();

    editPlanFields.forEach(section => {
        section.textFields.forEach(field => {
            initialValues[field.name] = data?.[field.name] ?? ''
        })
    })  

    const validationSchema = Yup.object({
        planName: Yup.string()
            .matches(/^[A-Za-z\s]+$/, "Name should only contain only letters")
            .required("Plan Name is required"),
        ...editPlanFields.reduce((name, section) => {
            section.textFields.forEach(field => {
                if (field.name !== 'planName') {
                    name[field.name] = Yup.string()
                        .matches(/^[0-9]*$/, 'Only number is allowed')
                        .required(`${field.name} is required`)
                }
            })
            return name
        }, {})
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            handleSavePlan(values)
        }
    })

    const handleClose = () => {
        formik.resetForm();
        onClose();
    }

    const handleSavePlan = async (formData) => {
        const { updatedAt, createdAt, createdBy, __v, _id, ...restData } = formData;
        delete (_id, updatedAt, createdAt, __v, createdBy);


        try {
            let response;
            if (mode === 'add') {
                response = await addPlan(formData).unwrap();
            } else if (mode === 'edit') {
                response = await updatePlan({ id: data?._id, ...restData }).unwrap();
            }
            if (response?.message) toast.success(response.message)
            await refetch();
            formik.resetForm();
            handleClose();
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || // axios/fetch style API errors
                error?.data?.message ||           // RTK Query or custom error
                error?.message ||                 // default JS error object
                "Something went wrong. Please try again."; // fallback

            toast.error(errorMessage);
        }
    };

    return (
        <>
            <Dialog onClose={handleClose} open={open}>
                <form
                    className={{ width: '500px' }} onSubmit={formik.handleSubmit}
                >
                    <Box
                        maxWidth='sm'
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            width: 'full',
                            margin: '1rem',
                            bgcolor: 'white',
                            padding: '1rem'
                        }}
                    >
                        <Typography
                            variant='h3'
                            component='h2'
                            sx={{ cursor: 'pointer', color: 'rgba(40, 40, 123, 1)' }}
                        >
                            {mode === 'add' ? 'Add Plan' : 'Edit Plan'}
                        </Typography>
                        {editPlanFields.map((section) => (
                            <Box key={section.title} sx={{ mt: 3 }}>
                                <Divider sx={{ mt: 3, mb: 2 }} textAlign='center'>
                                    <Typography variant='h6' color='textSecondary'>
                                        {section.title}
                                    </Typography>
                                </Divider>
                                <Grid container spacing={2}>
                                    {section.textFields?.map((field) => (
                                        <Grid item xs={12} md={6} key={field.name}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '16px',
                                                        fontWeight: 700,
                                                        lineHeight: '26px',
                                                        color: 'rgb(129, 129, 176)'
                                                    }}
                                                >
                                                    {field.label}
                                                </Typography>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                name={field.name}
                                                value={formik.values[field.name]}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                                                helperText={formik.touched[field.name] && formik.errors[field.name]}
                                                sx={{
                                                    '& div': {
                                                        pl: 0.3,
                                                        borderRadius: '0.7rem',
                                                        width: '20rem',
                                                        mt: '-0.2rem'
                                                    },
                                                    '& div fieldset': {
                                                        border: '1px solid #E4E4E5'
                                                    },
                                                    '& div input': {
                                                        py: 1.3,
                                                        fontSize: '13px',
                                                        fontWeight: 400,
                                                        lineHeight: '16px',
                                                        letterSpacing: '0em',
                                                        '&::placeholder': {
                                                            color: 'rgba(40, 40, 123, 0.5)'
                                                        }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'end', mt: '2rem', gap: 2 }}>
                            <Button onClick={handleClose} variant='outlined'>Cancel</Button>
                            <Button
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    py: 1,
                                    px: 2
                                }}
                                variant='contained'
                                type='submit'

                            >
                                {mode === 'add' ? 'Add' : 'Update'}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Dialog> 
        </>
    )
}